/**
 * FeeSetup — FM2B Fee Schedule List + FM2A Fee Type Config Entry
 *
 * Route: /finance/fee-setup
 * RBAC: finance.fee_schedule.view (registrar, finance_officer, finance_manager,
 *         cfo, institution_admin, super_admin, programme_manager)
 *       finance.fee_schedule.create (finance_manager, institution_admin, super_admin)
 *
 * FM2 GUARDRAILS:
 *   - No FeeAssessment generation on this page
 *   - Preview route → FeeScheduleDetail
 *   - Lifecycle: DRAFT → PUBLISHED → SUPERSEDED (DB canonical)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, RefreshCw, FileText, AlertCircle, Calendar,
  DollarSign, Eye, ChevronRight, Filter
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { CapabilityState } from "../components/shared/CapabilityState";
import { FormSection } from "../components/shared/FormSection";
import { FormActions } from "../components/shared/FormActions";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetFooter,
  SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

// ---- Permission guards --------------------------------------------------------
const VIEW_ROLES = new Set([
  "registrar", "finance_officer", "finance_manager", "cfo",
  "institution_admin", "super_admin", "programme_manager",
]);
const CREATE_ROLES = new Set(["finance_manager", "institution_admin", "super_admin"]);

// ---- Status helpers ----------------------------------------------------------
const STATUS_CONFIG = {
  DRAFT:      { label: "Draft",      variant: "secondary",    cls: "bg-slate-700 text-slate-200 border-slate-600" },
  PUBLISHED:  { label: "Published",  variant: "default",      cls: "bg-emerald-900 text-emerald-200 border-emerald-700" },
  SUPERSEDED: { label: "Superseded", variant: "destructive",  cls: "bg-slate-800 text-slate-400 border-slate-700 line-through" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ---- Create Schedule Form ---------------------------------------------------
function CreateScheduleSheet({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "", name: "", currency_id: "", academic_year_id: "", effective_from: "", effective_to: "",
  });
  const [currencies, setCurrencies] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [authorityState, setAuthorityState] = useState({ currencies: "OK", academic_years: "OK" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Load reference data
    Promise.all([
      api.get("/finance/admin/currencies"),
      api.get("/finance/admin/academic-years"),
    ]).then(([curRes, ayRes]) => {
      const curData = curRes.data;
      const ayData = ayRes.data;
      setCurrencies(curData.status === "OK" ? curData.items : []);
      setAcademicYears(ayData.status === "OK" ? ayData.items : []);
      setAuthorityState({
        currencies: curData.status || "OK",
        academic_years: ayData.status || "OK",
      });
    }).catch(() => {});
  }, [open]);

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.currency_id || !form.effective_from) {
      setError("Code, Name, Currency, and Effective From are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: form.code,
        name: form.name,
        currency_id: form.currency_id,
        effective_from: form.effective_from,
        ...(form.academic_year_id && form.academic_year_id !== "__none__" && { academic_year_id: form.academic_year_id }),
        ...(form.effective_to && { effective_to: form.effective_to }),
      };
      const res = await api.post("/finance/fee-schedules", payload);
      onCreated(res.data);
      onClose();
      setForm({ code: "", name: "", currency_id: "", academic_year_id: "", effective_from: "", effective_to: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create fee schedule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle data-testid="create-schedule-sheet-title">New Fee Schedule</SheetTitle>
          <SheetDescription>
            Creates a DRAFT fee schedule. Add rules after creation.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-schedule-form">
          <FormSection title="Identity" description="Unique code and display name for this fee schedule.">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fs-code">Code <span className="text-red-500">*</span></Label>
                <Input
                  id="fs-code"
                  data-testid="fee-schedule-code-input"
                  placeholder="PGP-2026-27"
                  value={form.code}
                  onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                  maxLength={60}
                  required
                />
                <p className="text-xs text-muted-foreground">Uppercase code. Must be unique per institution.</p>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="fs-name">Name <span className="text-red-500">*</span></Label>
                <Input
                  id="fs-name"
                  data-testid="fee-schedule-name-input"
                  placeholder="PGP Fees 2026-27"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  maxLength={200}
                  required
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Configuration" description="Currency and academic year binding.">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Currency <span className="text-red-500">*</span></Label>
                {authorityState.currencies === "NOT_CONFIGURED" ? (
                  <CapabilityState
                    state="NOT_CONFIGURED"
                    title="No currencies configured"
                    reason="Contact your system administrator to configure currencies."
                    compact
                    data-testid="currency-not-configured"
                  />
                ) : (
                  <Select
                    value={form.currency_id}
                    onValueChange={(v) => handleChange("currency_id", v)}
                    data-testid="fee-schedule-currency-select"
                  >
                    <SelectTrigger data-testid="fee-schedule-currency-trigger">
                      <SelectValue placeholder="Select currency..." />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`currency-option-${c.iso_code}`}>
                          {c.iso_code} — {c.name} {c.symbol && `(${c.symbol})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Academic Year <span className="text-slate-400 text-xs">(optional)</span></Label>
                {authorityState.academic_years === "NOT_CONFIGURED" ? (
                  <CapabilityState
                    state="NOT_CONFIGURED"
                    title="No academic years configured"
                    reason="Bind to an academic year once configured."
                    compact
                    data-testid="academic-year-not-configured"
                  />
                ) : (
                  <Select
                    value={form.academic_year_id}
                    onValueChange={(v) => handleChange("academic_year_id", v)}
                    data-testid="fee-schedule-academic-year-select"
                  >
                    <SelectTrigger data-testid="fee-schedule-academic-year-trigger">
                      <SelectValue placeholder="Select academic year..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" data-testid="no-academic-year">
                        No binding (all years)
                      </SelectItem>
                      {academicYears.map((ay) => (
                        <SelectItem key={ay.id} value={ay.id} data-testid={`ay-option-${ay.code}`}>
                          {ay.code} ({ay.start_date?.slice(0, 4)} – {ay.end_date?.slice(0, 4)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </FormSection>

          <FormSection title="Effective Period" description="When this schedule takes effect.">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fs-eff-from">Effective From <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  id="fs-eff-from"
                  data-testid="fee-schedule-effective-from-input"
                  value={form.effective_from}
                  onChange={(e) => handleChange("effective_from", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fs-eff-to">Effective To <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  type="date"
                  id="fs-eff-to"
                  data-testid="fee-schedule-effective-to-input"
                  value={form.effective_to}
                  onChange={(e) => handleChange("effective_to", e.target.value)}
                  min={form.effective_from}
                />
              </div>
            </div>
          </FormSection>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-sm" data-testid="create-schedule-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </form>

        <SheetFooter className="mt-6">
          <FormActions
            variant="dialog"
            onCancel={onClose}
            onSave={handleSubmit}
            saveLabel="Create Schedule"
            saving={saving}
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ---- Main Page ---------------------------------------------------------------
export default function FeeSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const canView   = VIEW_ROLES.has(user?.role);
  const canCreate = CREATE_ROLES.has(user?.role);

  const [schedules, setSchedules] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    const params = (statusFilter && statusFilter !== "__all__") ? `?status=${statusFilter}` : "";
    api
      .get(`/finance/fee-schedules${params}`)
      .then((r) => {
        setSchedules(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load fee schedules."))
      .finally(() => setLoading(false));
  }, [canView, statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return (
      <ResponsivePageContainer data-testid="fee-setup-no-access">
        <PageHeader title="Fee Setup" description="FM2: Fee Schedule configuration." />
        <CapabilityState
          state="PERMISSION_DENIED"
          title="Fee Setup Access Restricted"
          reason="Your role does not have finance.fee_schedule.view permission."
          data-testid="fee-setup-permission-denied"
        />
      </ResponsivePageContainer>
    );
  }

  return (
    <ResponsivePageContainer variant="wide" data-testid="fee-setup-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <PageHeader
          title="Fee Setup"
          description="Configure institutional fee schedules and rules."
        />
        {canCreate && (
          <Button
            onClick={() => setShowCreate(true)}
            data-testid="create-fee-schedule-btn"
            className="shrink-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Fee Schedule
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4" data-testid="fee-setup-filters">
        <Filter className="h-4 w-4 text-slate-400" />
        <Select value={statusFilter} onValueChange={setStatusFilter} data-testid="status-filter-select">
          <SelectTrigger className="w-44" data-testid="status-filter-trigger">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="filter-all">All statuses</SelectItem>
            <SelectItem value="DRAFT" data-testid="filter-draft">Draft</SelectItem>
            <SelectItem value="PUBLISHED" data-testid="filter-published">Published</SelectItem>
            <SelectItem value="SUPERSEDED" data-testid="filter-superseded">Superseded</SelectItem>
          </SelectContent>
        </Select>
        {loading && <RefreshCw className="h-4 w-4 text-slate-400 animate-spin" />}
        <span className="text-xs text-muted-foreground ml-auto">{total} schedule{total !== 1 ? "s" : ""}</span>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-sm mb-4" data-testid="fee-setup-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && schedules.length === 0 && (
        <CapabilityState
          state="NOT_CONFIGURED"
          title={(statusFilter && statusFilter !== "__all__") ? `No ${statusFilter.toLowerCase()} fee schedules` : "No fee schedules yet"}
          reason={
            canCreate
              ? "Create your first fee schedule to start configuring institutional fees."
              : "No fee schedules have been configured for this institution yet."
          }
          data-testid="fee-setup-empty-state"
        />
      )}

      {/* Schedule table */}
      {schedules.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden" data-testid="fee-schedule-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Code</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider hidden md:table-cell">Academic Year</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider hidden sm:table-cell">Currency</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider hidden lg:table-cell">Effective From</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider hidden lg:table-cell">Rules</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {schedules.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-muted/20 cursor-pointer transition-colors"
                  onClick={() => navigate(`/finance/fee-setup/${s.id}`)}
                  data-testid={`fee-schedule-row-${s.code}`}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-semibold text-slate-300">{s.code}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground">{s.name}</span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {s.academic_year_code ? (
                      <span className="inline-flex items-center gap-1 text-slate-300">
                        <Calendar className="h-3 w-3 text-slate-500" />
                        {s.academic_year_code}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="inline-flex items-center gap-1 text-slate-300">
                      <DollarSign className="h-3 w-3 text-slate-500" />
                      {s.currency_iso || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs hidden lg:table-cell">
                    {s.effective_from || "—"}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="inline-flex items-center gap-1 text-slate-300">
                      <FileText className="h-3 w-3 text-slate-500" />
                      {s.rule_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="h-4 w-4 text-slate-500 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Sheet */}
      <CreateScheduleSheet
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(data) => {
          load();
          navigate(`/finance/fee-setup/${data.id}`);
        }}
      />
    </ResponsivePageContainer>
  );
}
